import { forwardRef, useState } from "react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { getInitials, getAvatarColor } from "@/lib/avatar"
import { getUserDisplayName } from "@/lib/user-display"
import { useAuthStore } from "@/stores/auth-store"
import { Check, UserPlus, UserRoundPlus, Plus, X } from "lucide-react"
import type { SectionAssignment } from "@/stores/section-assignments-store"

// ===================
// Shared Types
// ===================

export interface BidMemberInfo {
  userId: string
  role: string
  email: string
  fullName: string | null
  avatarUrl: string | null
}

// ===================
// Member Avatar
// ===================

export function MemberAvatar({ user, size = "sm" }: {
  user: { userId: string; fullName: string | null; avatarUrl: string | null }
  size?: "xs" | "sm" | "md"
}) {
  const sizeClasses = size === "xs" ? "h-5 w-5" : size === "sm" ? "h-6 w-6" : "h-7 w-7"
  const textClasses = size === "xs" ? "text-[9px]" : size === "sm" ? "text-[10px]" : "text-xs"
  const color = getAvatarColor(user.userId)

  if (user.avatarUrl) {
    return (
      <img
        src={user.avatarUrl}
        alt={user.fullName ?? "User"}
        className={cn("rounded-full object-cover shrink-0", sizeClasses)}
      />
    )
  }

  return (
    <div
      className={cn(
        "rounded-full flex items-center justify-center font-medium shrink-0",
        color.bg, color.text, sizeClasses, textClasses
      )}
    >
      {getInitials(user.fullName)}
    </div>
  )
}

// ===================
// Artifact Owner Selector
// ===================

interface ArtifactOwnerSelectorProps {
  artifact: { ownerId: string | null; ownerName: string | null; ownerAvatarUrl: string | null }
  bidMembers: BidMemberInfo[]
  canSetOwner: boolean
  onSetOwner: (userId: string | null) => void
  /** When true, renders just the avatar (no "Owner:" label) with tooltip. Used inline in page headers. */
  compact?: boolean
}

export function ArtifactOwnerSelector({ artifact, bidMembers, canSetOwner, onSetOwner, compact = false }: ArtifactOwnerSelectorProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const currentUserId = useAuthStore((s) => s.user?.id ?? null)
  const hasOwner = !!artifact.ownerId

  const ownerLabel = hasOwner
    ? getUserDisplayName(
        { userId: artifact.ownerId, fullName: artifact.ownerName },
        currentUserId,
        { fallback: 'Owner' }
      )
    : ''

  const filteredMembers = bidMembers.filter((m) => {
    if (!search) return true
    const q = search.toLowerCase()
    return m.fullName?.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
  })

  const handleSelect = (userId: string | null) => {
    onSetOwner(userId)
    setOpen(false)
    setSearch("")
  }

  if (!canSetOwner) {
    // Read-only view
    if (!hasOwner) return null
    if (compact) {
      return (
        <div title={`Owner: ${ownerLabel}`}>
          <MemberAvatar user={{ userId: artifact.ownerId!, fullName: artifact.ownerName, avatarUrl: artifact.ownerAvatarUrl }} />
        </div>
      )
    }
    return (
      <div className="flex items-center gap-1.5" title={ownerLabel || undefined}>
        <span className="text-meta">Owner:</span>
        <MemberAvatar user={{ userId: artifact.ownerId!, fullName: artifact.ownerName, avatarUrl: artifact.ownerAvatarUrl }} />
      </div>
    )
  }

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch("") }}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "flex items-center rounded-lg transition-colors hover:bg-muted",
            compact ? "p-0.5" : "gap-1.5 pl-0 pr-2 py-1 text-xs",
            !hasOwner && !compact && "text-muted-foreground"
          )}
          title={hasOwner ? `Owner: ${ownerLabel}` : "Set document owner"}
        >
          {!compact && <span>Owner:</span>}
          {hasOwner ? (
            <MemberAvatar user={{ userId: artifact.ownerId!, fullName: artifact.ownerName, avatarUrl: artifact.ownerAvatarUrl }} />
          ) : (
            <div className="h-6 w-6 rounded-full border border-dashed border-muted-foreground/40 flex items-center justify-center">
              <UserRoundPlus className="h-3 w-3 text-subtle" />
            </div>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-0">
        <div className="p-2 border-b border-border">
          <Input
            placeholder="Search members..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 text-xs"
          />
        </div>
        <div className="max-h-48 overflow-y-auto p-1">
          {hasOwner && (
            <button
              onClick={() => handleSelect(null)}
              className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors text-muted-foreground"
            >
              <X className="h-3.5 w-3.5" />
              <span>Remove owner</span>
            </button>
          )}
          {filteredMembers.length === 0 && (
            <div className="px-3 py-4 text-center text-meta">No members found</div>
          )}
          {filteredMembers.map((member) => {
            const isSelected = member.userId === artifact.ownerId
            const memberLabel = getUserDisplayName(
              { userId: member.userId, fullName: member.fullName, email: member.email },
              currentUserId
            )
            return (
              <button
                key={member.userId}
                onClick={() => handleSelect(member.userId)}
                className={cn(
                  "flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors",
                  isSelected && "bg-muted/50"
                )}
              >
                <MemberAvatar user={member} />
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-sm truncate">{memberLabel}</div>
                  {member.fullName && (
                    <div className="text-meta truncate">{member.email}</div>
                  )}
                </div>
                {isSelected && <Check className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ===================
// Member picker pieces (section owner / planned reviewer)
// ===================

/**
 * Search + member list shared by the section Owner and Reviewer pickers, so
 * both columns choose people the same way. `removeLabel` adds a first row that
 * clears the current value.
 */
function MemberPickerList({
  members,
  selectedUserId,
  currentUserId,
  removeLabel,
  onRemove,
  onSelect,
}: {
  members: BidMemberInfo[]
  selectedUserId: string | null | undefined
  currentUserId: string | null | undefined
  removeLabel?: string
  onRemove?: () => void
  onSelect: (userId: string) => void
}) {
  const [search, setSearch] = useState("")

  const filteredMembers = members.filter((m) => {
    if (!search) return true
    const q = search.toLowerCase()
    return m.fullName?.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
  })

  return (
    <>
      <div className="p-2 border-b border-border">
        <Input
          placeholder="Search members..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-7 text-xs"
        />
      </div>
      <div className="max-h-48 overflow-y-auto p-1">
        {removeLabel && onRemove && (
          <button
            onClick={onRemove}
            className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors text-muted-foreground"
          >
            <X className="h-3.5 w-3.5" />
            <span>{removeLabel}</span>
          </button>
        )}
        {filteredMembers.length === 0 && (
          <div className="px-3 py-4 text-center text-meta">No members found</div>
        )}
        {filteredMembers.map((member) => {
          const isSelected = member.userId === selectedUserId
          const memberLabel = getUserDisplayName(
            { userId: member.userId, fullName: member.fullName, email: member.email },
            currentUserId
          )
          return (
            <button
              key={member.userId}
              onClick={() => onSelect(member.userId)}
              className={cn(
                "flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors",
                isSelected && "bg-muted/50"
              )}
            >
              <MemberAvatar user={member} />
              <div className="flex-1 min-w-0 text-left">
                <div className="text-sm truncate">{memberLabel}</div>
                {member.fullName && (
                  <div className="text-meta truncate">{member.email}</div>
                )}
              </div>
              {isSelected && <Check className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
            </button>
          )
        })}
      </div>
    </>
  )
}

/**
 * Avatar + name, the Pack's owner look. The name takes all the width the cell
 * has and only truncates when it genuinely doesn't fit (full name in the
 * tooltip). `hint` is a small caption under the name, so it never competes
 * with the name for width.
 */
function PersonChip({ user, label, hint }: {
  user: { userId: string; fullName: string | null; avatarUrl: string | null }
  label: string
  hint?: string
}) {
  return (
    <span className="flex min-w-0 max-w-full items-center gap-1.5">
      <MemberAvatar user={user} size="xs" />
      <span className="flex min-w-0 flex-1 flex-col text-left leading-tight">
        <span className="truncate text-sm" title={label}>{label}</span>
        {hint && <span className="text-meta">{hint}</span>}
      </span>
    </span>
  )
}

/**
 * Empty owner / reviewer affordance: an icon-only "add person" button. The
 * full meaning ("Assign owner", "Claim this section", …) lives in the
 * aria-label and the tooltip, so the cell stays a quiet 28px mark.
 *
 * Forwards its ref and props to the Button so it can sit inside a
 * `PopoverTrigger asChild`.
 */
const EmptyPickerTrigger = forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<typeof Button> & { label: string }>(
  function EmptyPickerTrigger({ label, className, ...props }, ref) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              ref={ref}
              type="button"
              variant="ghost"
              size="icon"
              aria-label={label}
              className={cn("h-7 w-7 text-subtle hover:text-foreground", className)}
              {...props}
            >
              <UserPlus className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }
)

/** Clickable wrapper around a PersonChip that opens a picker. */
const ChipPickerTrigger = forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<"button">>(
  function ChipPickerTrigger({ className, children, ...props }, ref) {
    return (
      <button
        ref={ref}
        className={cn("flex w-full min-w-0 items-center rounded-md px-1 py-0.5 hover:bg-muted transition-colors", className)}
        {...props}
      >
        {children}
      </button>
    )
  }
)

// ===================
// Section Owner Cell
// ===================

interface SectionOwnerCellProps {
  assignment: { userId: string; fullName: string | null; avatarUrl: string | null } | undefined
  bidMembers: BidMemberInfo[]
  canAssign: boolean
  currentUserId: string | undefined
  onAssign: (userId: string) => void
  onUnassign: () => void
  isFinalized: boolean
  /**
   * `avatar` (default) — the compact avatar / dashed "+" circle.
   * `labelled` — the Pack's look: avatar + name when set, an icon-only
   * "add person" button when empty. Used by the response outline table.
   */
  appearance?: "avatar" | "labelled"
}

export function SectionOwnerCell({
  assignment,
  bidMembers,
  canAssign,
  currentUserId,
  onAssign,
  onUnassign,
  isFinalized,
  appearance = "avatar",
}: SectionOwnerCellProps) {
  const [open, setOpen] = useState(false)
  const isLabelled = appearance === "labelled"

  // The optimistic row written on assign carries only the user id, so its
  // initials would fall through to "?" until the server response lands.
  // Backfill the identity from the already-loaded bid members.
  const resolved = assignment
    ? (() => {
        const member = bidMembers.find((m) => m.userId === assignment.userId)
        if (!member) return assignment
        return {
          ...assignment,
          fullName: assignment.fullName ?? member.fullName,
          avatarUrl: assignment.avatarUrl ?? member.avatarUrl,
        }
      })()
    : undefined
  const hasAssignment = !!resolved

  // Contributors can only self-claim unassigned sections
  const canSelfClaim = !canAssign && !hasAssignment && !isFinalized

  const handleAssign = (userId: string) => {
    onAssign(userId)
    setOpen(false)
  }

  const handleUnassign = () => {
    onUnassign()
    setOpen(false)
  }

  const assignedLabel = resolved
    ? getUserDisplayName(
        { userId: resolved.userId, fullName: resolved.fullName },
        currentUserId,
        { fallback: 'Assigned' }
      )
    : null

  const readOnlyAssigned = resolved
    ? isLabelled
      ? (
        <div title={assignedLabel ?? undefined} className="min-w-0">
          <PersonChip user={resolved} label={assignedLabel ?? 'Assigned'} />
        </div>
      )
      : (
        <div title={assignedLabel ?? undefined}>
          <MemberAvatar user={{ userId: resolved.userId, fullName: resolved.fullName, avatarUrl: resolved.avatarUrl }} />
        </div>
      )
    : null

  // Read-only: show avatar or nothing
  if (isFinalized && !hasAssignment) return <span className="text-meta">—</span>
  if (isFinalized && hasAssignment) return readOnlyAssigned

  // Self-claim only (contributor, unassigned)
  if (canSelfClaim) {
    if (isLabelled) {
      return (
        <EmptyPickerTrigger
          label="Claim this section"
          onClick={() => currentUserId && onAssign(currentUserId)}
        />
      )
    }
    return (
      <button
        onClick={() => currentUserId && onAssign(currentUserId)}
        className="h-6 w-6 rounded-full border border-dashed border-muted-foreground/30 flex items-center justify-center hover:border-muted-foreground/60 hover:bg-muted/50 transition-colors"
        title="Claim this section"
      >
        <Plus className="h-3 w-3 text-subtle" />
      </button>
    )
  }

  // Not assignable and not self-claimable — just show state
  if (!canAssign && hasAssignment) return readOnlyAssigned

  if (!canAssign) {
    return <span className="text-meta">—</span>
  }

  const trigger = hasAssignment
    ? isLabelled
      ? (
        <ChipPickerTrigger title={`${assignedLabel ?? "Assigned"} — click to change`}>
          <PersonChip user={resolved} label={assignedLabel ?? "Assigned"} />
        </ChipPickerTrigger>
      )
      : (
        <button
          className="rounded-full hover:ring-2 hover:ring-muted transition-all"
          title={`${assignedLabel ?? "Assigned"} — click to change`}
        >
          <MemberAvatar user={{ userId: resolved.userId, fullName: resolved.fullName, avatarUrl: resolved.avatarUrl }} />
        </button>
      )
    : isLabelled
      ? <EmptyPickerTrigger label="Assign owner" />
      : (
        <button
          className="h-6 w-6 rounded-full border border-dashed border-muted-foreground/30 flex items-center justify-center hover:border-muted-foreground/60 hover:bg-muted/50 transition-colors"
          title="Assign owner"
        >
          <Plus className="h-3 w-3 text-subtle" />
        </button>
      )

  // Full assignment popover (bid owner / artifact owner / admin)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-0">
        <MemberPickerList
          members={bidMembers}
          selectedUserId={assignment?.userId}
          currentUserId={currentUserId}
          {...(hasAssignment ? { removeLabel: "Unassign", onRemove: handleUnassign } : {})}
          onSelect={handleAssign}
        />
      </PopoverContent>
    </Popover>
  )
}

// ===================
// Section Reviewer Cell
// ===================
//
// Two kinds of reviewer, and the requested one wins:
// - requested (`reviewerId`) — set by "Request review" in the editor, which
//   notifies and opens a round. Read-only here.
// - planned (`plannedReviewerId`) — a quiet plan made from this table: no
//   notification, no round, no status change. Owners of the document/bid can
//   set, change or remove it; "Request review" lists them first.

interface SectionReviewerCellProps {
  /** The requested reviewer (review actually asked for). */
  reviewerId: string | null
  /** The planned reviewer (a plan, not a request). */
  plannedReviewerId?: string | null
  plannedReviewerName?: string | null
  bidMembers: BidMemberInfo[]
  currentUserId?: string | undefined
  /** The section owner — never offered as its own reviewer. */
  ownerUserId?: string | null
  /** Who may plan: the document owner or a bid owner (mirrors owner assignment). */
  canPlan?: boolean
  isFinalized?: boolean
  onPlan?: (userId: string | null) => void
}

export function SectionReviewerCell({
  reviewerId,
  plannedReviewerId = null,
  plannedReviewerName = null,
  bidMembers,
  currentUserId,
  ownerUserId = null,
  canPlan = false,
  isFinalized = false,
  onPlan,
}: SectionReviewerCellProps) {
  const [open, setOpen] = useState(false)

  const personFor = (userId: string, fallbackName: string | null) => {
    const member = bidMembers.find((m) => m.userId === userId)
    return {
      user: { userId, fullName: member?.fullName ?? fallbackName, avatarUrl: member?.avatarUrl ?? null },
      label: getUserDisplayName(
        { userId, fullName: member?.fullName ?? fallbackName, email: member?.email ?? null },
        currentUserId,
        { fallback: 'Reviewer' }
      ),
    }
  }

  // A requested review wins over the plan.
  if (reviewerId) {
    const { user, label } = personFor(reviewerId, null)
    return (
      <div title={`Reviewer: ${label}`} className="min-w-0">
        <PersonChip user={user} label={label} />
      </div>
    )
  }

  const canEdit = canPlan && !isFinalized && !!onPlan
  const planned = plannedReviewerId ? personFor(plannedReviewerId, plannedReviewerName) : null

  if (!canEdit) {
    if (!planned) return <span className="text-meta">—</span>
    return (
      <div title={`Planned reviewer: ${planned.label}`} className="min-w-0">
        <PersonChip user={planned.user} label={planned.label} hint="planned" />
      </div>
    )
  }

  const candidates = bidMembers.filter((m) => m.role !== 'viewer' && m.userId !== ownerUserId)

  const handleSelect = (userId: string | null) => {
    onPlan(userId)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {planned ? (
          <ChipPickerTrigger title={`Planned reviewer: ${planned.label} — click to change`}>
            <PersonChip user={planned.user} label={planned.label} hint="planned" />
          </ChipPickerTrigger>
        ) : (
          <EmptyPickerTrigger label="Plan a reviewer — no one is notified" />
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-0">
        <MemberPickerList
          members={candidates}
          selectedUserId={plannedReviewerId}
          currentUserId={currentUserId}
          {...(planned ? { removeLabel: "Remove", onRemove: () => handleSelect(null) } : {})}
          onSelect={(userId) => handleSelect(userId)}
        />
      </PopoverContent>
    </Popover>
  )
}

// ===================
// Sidebar Assignment Avatar
// ===================

function SidebarAvatarInner({ assignment }: {
  assignment: SectionAssignment
}) {
  const aColor = getAvatarColor(assignment.userId)
  return (
    <div className="h-5 w-5 rounded-full flex items-center justify-center shrink-0 overflow-hidden">
      {assignment.avatarUrl ? (
        <img src={assignment.avatarUrl} alt={assignment.fullName ?? "User"} className="h-5 w-5 rounded-full object-cover" />
      ) : (
        <div className={cn("h-5 w-5 rounded-full flex items-center justify-center", aColor.bg)}>
          <span className={cn("text-[9px] font-medium", aColor.text)}>{getInitials(assignment.fullName)}</span>
        </div>
      )}
    </div>
  )
}

function UnassignedPlaceholder({ interactive }: { interactive?: boolean }) {
  return (
    <div className={cn(
      "h-5 w-5 rounded-full border border-dashed border-muted-foreground/30 flex items-center justify-center",
      interactive && "hover:border-muted-foreground/60 hover:bg-muted/50 transition-colors"
    )}>
      <Plus className="h-2.5 w-2.5 text-subtle" />
    </div>
  )
}

interface SidebarAssignmentAvatarProps {
  assignment: SectionAssignment | undefined
  currentUserId: string | undefined
  bidMembers: BidMemberInfo[]
  canAssign: boolean
  isFinalized: boolean
  onAssign: (userId: string) => void
  onUnassign: () => void
  itemLabel?: string
}

export function SidebarAssignmentAvatar({
  assignment,
  currentUserId,
  bidMembers,
  canAssign,
  isFinalized,
  onAssign,
  onUnassign,
  itemLabel = "section",
}: SidebarAssignmentAvatarProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const hasAssignment = !!assignment?.userId

  // The optimistic row written on assign carries only the user id, so its
  // initials would fall through to "?" until the server response lands.
  // Backfill the identity from the already-loaded bid members.
  const resolvedAssignment = assignment
    ? (() => {
        const member = bidMembers.find((m) => m.userId === assignment.userId)
        if (!member) return assignment
        return {
          ...assignment,
          fullName: assignment.fullName ?? member.fullName,
          email: assignment.email || member.email,
          avatarUrl: assignment.avatarUrl ?? member.avatarUrl,
        }
      })()
    : undefined

  const filteredMembers = bidMembers.filter((m) => {
    if (!search) return true
    const q = search.toLowerCase()
    return m.fullName?.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
  })

  const assignedLabel = resolvedAssignment
    ? getUserDisplayName(
        { userId: resolvedAssignment.userId, fullName: resolvedAssignment.fullName, email: resolvedAssignment.email },
        currentUserId,
        { fallback: 'Assigned' }
      )
    : null

  // 1. Finalized — read-only
  if (isFinalized) {
    return (
      <div className="shrink-0">
        {resolvedAssignment?.userId ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div><SidebarAvatarInner assignment={resolvedAssignment} /></div>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p className="text-xs">Assigned to {assignedLabel}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <UnassignedPlaceholder />
        )}
      </div>
    )
  }

  // 2. Contributor + assigned → read-only avatar
  if (!canAssign && resolvedAssignment?.userId) {
    return (
      <div className="shrink-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <div><SidebarAvatarInner assignment={resolvedAssignment} /></div>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p className="text-xs">Assigned to {assignedLabel}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    )
  }

  // 3. Contributor + unassigned → self-claim with confirmation popover
  if (!canAssign && !hasAssignment) {
    return (
      <div className="shrink-0">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              title={`Click to claim this ${itemLabel}`}
            >
              <UnassignedPlaceholder interactive />
            </button>
          </PopoverTrigger>
          <PopoverContent side="right" align="start" className="w-48 p-3" onClick={(e) => e.stopPropagation()}>
            <p className="text-meta mb-2">Assign to yourself?</p>
            <Button
              size="sm"
              className="w-full h-7 text-xs"
              onClick={() => {
                if (currentUserId) onAssign(currentUserId)
                setOpen(false)
              }}
            >
              Claim {itemLabel}
            </Button>
          </PopoverContent>
        </Popover>
      </div>
    )
  }

  // 4. Owner — full assignment popover
  if (canAssign) {
    return (
      <div className="shrink-0">
        <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch("") }}>
          <PopoverTrigger asChild>
            {resolvedAssignment?.userId ? (
              <button
                className="rounded-full hover:ring-2 hover:ring-muted transition-all"
                title={`${assignedLabel ?? "Assigned"} — click to change`}
                onClick={(e) => e.stopPropagation()}
              >
                <SidebarAvatarInner assignment={resolvedAssignment} />
              </button>
            ) : (
              <button
                onClick={(e) => e.stopPropagation()}
                title={`Assign ${itemLabel}`}
              >
                <UnassignedPlaceholder interactive />
              </button>
            )}
          </PopoverTrigger>
          <PopoverContent side="right" align="start" className="w-56 p-0" onClick={(e) => e.stopPropagation()}>
            <div className="p-2 border-b border-border">
              <Input
                placeholder="Search members..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-7 text-xs"
              />
            </div>
            <div className="max-h-48 overflow-y-auto p-1">
              {hasAssignment && (
                <button
                  onClick={() => { onUnassign(); setOpen(false); setSearch("") }}
                  className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors text-muted-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                  <span>Unassign</span>
                </button>
              )}
              {filteredMembers.length === 0 && (
                <div className="px-3 py-4 text-center text-meta">No members found</div>
              )}
              {filteredMembers.map((member) => {
                const isSelected = member.userId === assignment?.userId
                const memberLabel = getUserDisplayName(
                  { userId: member.userId, fullName: member.fullName, email: member.email },
                  currentUserId
                )
                return (
                  <button
                    key={member.userId}
                    onClick={() => { onAssign(member.userId); setOpen(false); setSearch("") }}
                    className={cn(
                      "flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors",
                      isSelected && "bg-muted/50"
                    )}
                  >
                    <MemberAvatar user={member} />
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-sm truncate">{memberLabel}</div>
                      {member.fullName && (
                        <div className="text-meta truncate">{member.email}</div>
                      )}
                    </div>
                    {isSelected && <Check className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                  </button>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    )
  }

  // Fallback: non-interactive placeholder
  return (
    <div className="shrink-0">
      <UnassignedPlaceholder />
    </div>
  )
}
